import { m, AnimatePresence } from 'framer-motion';
import { ShieldCheck, ShieldAlert, Loader2, Monitor, AlertTriangle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * BaseStatusLayout - Shared layout for status feedbacks to ensure consistency.
 * Supports theme variants and provides entry animations.
 */
const BaseStatusLayout = ({ children, variant = 'dark', fullScreen = true }) => {
    const isDark = variant === 'dark';
    
    // Industrial panels often have high contrast backgrounds
    const containerClasses = fullScreen 
        ? "fixed inset-0 z-[1000] flex items-center justify-center p-6" 
        : "w-full py-20 flex items-center justify-center p-6";

    return (
        <div className={`${containerClasses} ${isDark ? 'bg-primary' : 'bg-slate-50/80 backdrop-blur-md'}`}>
            <m.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="flex flex-col items-center gap-8 max-w-md text-center"
            >
                {children}
            </m.div>
        </div>
    );
};

/**
 * LoadingOverlay - Premium industrial loading feedback.
 * Supports theme variants and dynamic rotating messages for "live-system" feel.
 */
export const LoadingOverlay = ({ 
    title = "Initializing", 
    subtitle = "Syncing Core Services...", 
    icon: Icon = Loader2,
    accentIcon: AccentIcon = Monitor,
    fullScreen = true,
    variant = 'dark',
    user // Backward compatibility for Withdrawal Terminal context
}) => {
    const [messageIndex, setMessageIndex] = useState(0);
    const messages = Array.isArray(subtitle) ? subtitle : [subtitle];
    const isDark = variant === 'dark';

    // Auto-rotate messages if an array is provided to show system activity
    useEffect(() => {
        if (messages.length <= 1) return;
        const interval = setInterval(() => {
            setMessageIndex((prev) => (prev + 1) % messages.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [messages.length]);

    // Backward compatibility logic for Withdrawal Terminal
    const getFinalSubtitle = () => {
        if (subtitle === "Syncing Core Services..." && user !== undefined) {
            return !user ? 'Securing Terminal Connection...' : 'Syncing Local Inventory Cache...';
        }
        return messages[messageIndex];
    };

    return (
        <BaseStatusLayout variant={variant} fullScreen={fullScreen}>
            <div className="relative">
                {/* Premium Glow Effect: Machinery Aura */}
                <m.div
                    animate={{ 
                        scale: [1, 1.25, 1], 
                        opacity: isDark ? [0.15, 0.35, 0.15] : [0.3, 0.5, 0.3] 
                    }}
                    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                    className={`absolute inset-[-40px] blur-[60px] rounded-full 
                        ${isDark ? 'bg-accent/20' : 'bg-accent/40'}`}
                />
                
                <m.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                    className={`w-28 h-28 rounded-[2.5rem] border-4 border-dashed relative z-10
                        ${isDark ? 'border-accent/30' : 'border-accent/40'} 
                        flex items-center justify-center`}
                >
                    <Icon className="w-14 h-14 text-accent animate-spin" />
                </m.div>
                
                {AccentIcon && (
                    <AccentIcon 
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-accent opacity-50 z-20 pointer-events-none" 
                        size={32} 
                    />
                )}
            </div>

            <div className="flex flex-col items-center gap-3 relative z-10">
                <span className={`font-black text-3xl uppercase tracking-[0.6em] animate-pulse italic 
                    ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {title}
                </span>
                <AnimatePresence mode="wait">
                    <m.span 
                        key={messageIndex}
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 0.6, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.4 }}
                        className={`text-[10px] font-black uppercase tracking-[0.4em] 
                            ${isDark ? 'text-accent' : 'text-primary'}`}
                    >
                        {getFinalSubtitle()}
                    </m.span>
                </AnimatePresence>
            </div>
        </BaseStatusLayout>
    );
};

/**
 * RestrictedAccess - Security overlay for unauthorized or misconfigured states.
 * Features severity levels: warning (yellow) vs critical (red).
 */
export const RestrictedAccess = ({ 
    title, 
    message,
    error, // Auth error code (string)
    productsError, // Firestore error description (string)
    onRetry = () => window.location.reload(),
    showAdminAuth = true,
    isActivationRequired = false,
    severity = 'critical', // 'critical' | 'warning'
    variant = 'dark'
}) => {
    const navigate = useNavigate();
    const isDark = variant === 'dark';
    
    // Auto-detect based on common Firebase security patterns
    const isSetupRequired = isActivationRequired || error === 'auth/admin-restricted-operation';
    const finalSeverity = isSetupRequired ? 'warning' : severity;
    const isCritical = finalSeverity === 'critical';

    const finalTitle = title || (isSetupRequired ? 'System Activation Required' : 'Terminal Restricted');
    
    const finalMessage = message || (isSetupRequired 
        ? 'Terminal logic is ready but needs a Firebase switch enabled.'
        : (productsError?.includes('PERMISSION_DENIED')
            ? 'Security Protocol Triggered: This terminal requires manual activation by an administrator.'
            : 'Database Connection Lost: Please verify network stability.'));

    return (
        <BaseStatusLayout variant={variant}>
            {/* Visual Alarm Badge */}
            <div className={`p-12 rounded-[4.5rem] border-4 shadow-2xl animate-in zoom-in duration-500 relative
                ${isCritical 
                    ? 'bg-red-500/10 border-red-500/20 text-red-500 shadow-red-500/10' 
                    : 'bg-accent/10 border-accent/20 text-accent shadow-accent/10'}`}
            >
                {/* Background pulse for industrial alert feeling */}
                <m.div
                    animate={{ opacity: [0.05, 0.15, 0.05] }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className={`absolute inset-0 rounded-[4rem] ${isCritical ? 'bg-red-500' : 'bg-accent'}`}
                />
                
                <div className="relative z-10">
                    {isSetupRequired 
                        ? <ShieldCheck size={80} strokeWidth={1.5} /> 
                        : (isCritical ? <ShieldAlert size={80} strokeWidth={1.5} /> : <AlertTriangle size={80} strokeWidth={1.5} />)
                    }
                </div>
            </div>

            <div className="flex flex-col gap-6 italic italic-shaping">
                <h2 className={`text-4xl font-black uppercase tracking-tighter leading-none
                    ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {finalTitle}
                </h2>
                <div className="flex flex-col gap-4">
                    <p className={`text-[12px] font-black uppercase tracking-[0.5em] leading-relaxed opacity-80
                        ${isDark ? 'text-white' : 'text-slate-600'}`}>
                        {finalMessage}
                    </p>
                    
                    {error && !isSetupRequired && (
                        <div className={`text-[9px] font-mono p-4 rounded-2xl border 
                            ${isDark 
                                ? 'bg-red-500/5 border-red-500/10 text-red-400/80 shadow-inner' 
                                : 'bg-red-50 border-red-100 text-red-600 shadow-sm'}`}>
                            SYSTEM_FAULT_CODE: <span className="font-bold underline tracking-[0.2em]">{error}</span>
                        </div>
                    )}

                    {isSetupRequired && (
                        <div className={`mt-2 p-5 rounded-[2.5rem] border text-[10px] font-black uppercase tracking-[0.25em] text-left
                            shadow-inner transition-all
                            ${isDark 
                                ? 'bg-white/5 border-white/10 text-accent' 
                                : 'bg-accent/5 border-accent/10 text-primary'}`}>
                            <p className="mb-3 opacity-50 border-b border-current pb-2 inline-block">Manual Restoration Guide:</p>
                            <ol className="space-y-2 opacity-90">
                                <li className="flex gap-3"><span>01.</span> Access Firebase Management Console</li>
                                <li className="flex gap-3"><span>02.</span> Navigate to Auth &gt; Sign-in Providers</li>
                                <li className="flex gap-3"><span>03.</span> Toggle "Anonymous" Identity Protocol</li>
                            </ol>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-col gap-3 w-full mt-6">
                {onRetry && (
                    <button
                        onClick={onRetry}
                        className={`w-full py-6 font-black rounded-full uppercase tracking-[0.4em] text-[10px] transition-all border-2
                            ${isDark 
                                ? 'bg-white/5 border-white/10 text-white hover:bg-white/10 shadow-lg' 
                                : 'bg-slate-100 border-slate-200 text-slate-900 hover:bg-slate-200 shadow-sm'}`}
                    >
                        Retry Sequence
                    </button>
                )}
                {showAdminAuth && (
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full py-6 bg-accent text-primary font-black rounded-full uppercase tracking-[0.4em] text-[10px] 
                            shadow-[0_20px_40px_rgba(255,184,0,0.3)] transition-all hover:scale-[1.02] active:scale-95 italic"
                    >
                        Override Security
                    </button>
                )}
            </div>
        </BaseStatusLayout>
    );
};

